import { useLazyQuery } from "@apollo/client";
import { ReactJSXElement } from "@emotion/react/types/jsx-namespace";
import { Box, Grid, Stack, styled, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Button from "../../components/Button/Button";
import { ConfirmationModel } from "../../components/shared/ConfirmationModel/ConfirmationModel";
import InactivateAccountBanner from "../../components/shared/InactiveAccountBanner/InactiveAccountBanner";
import { LoadingSpinner } from "../../components/shared/LoadingSpinner/LoadingSpinner";
import { Spinner } from "../../components/shared/LoadingSpinner/Spinner";
import { NeedHelpCard } from "../../components/shared/NeedHelpCard/NeedHelpCard";
import { AutoPay } from "../../components/shared/TotalBalance/AutoPay/AutoPay";
import { PaperLessBilling } from "../../components/shared/TotalBalance/PaperLessBilling/PaperLessBilling";
import {
  ACCOUNT,
  ACCOUNT_SCREEN_COMPONENTS,
  AUTOPAY_PAPERLESS_LOADING_MESSAGE,
  isAdmin,
} from "../../Constants/constants";
import { AccountBillingPreference } from "../../Pages/Account/Components/BillingPref/BillingPref";
import { AccountProfile } from "../../Pages/Account/Components/Profile/Profile";
import { getBannerMessageByKey, getFlags, splitMessageBybraces } from "../../Services/helper";
import {
  CardHeading,
  PageGrid,
  PageNameTypography,
  StickyBox,
  StyledCard,
} from "../../Styles/GlobalStyles";
import { Theme } from "../../Styles/GlobalTheme";
import { HomepageNewBanners } from "../Home/Components/HomepageNewBanners/HomepageNewBanners";
import { Settings } from "./Components/AccountSettings/Settings";
import EquipmentReturnInfo from "./Components/EquipmentReturnInfo/EquipmentReturnInfo";
import { KeyAccountInfo } from "./Components/KeyAccountInfo/KeyAccountInfo";
import OrderAndServiceTicketHistory from "./Components/OrderAndServiceHistory/OrderAndServiceTicketHistoryRedirect";
import { ErrorPopup } from "./Components/OrderUpgrades/ErrorPopup";
import { GET_CUSTOMER_SERVICES_API } from "./Components/OrderUpgrades/query";
import { AccountPlansServices } from "./Components/PlansServices/PlansServices";
import { GET_BILLING_PREFERENCES, GET_NOTIFICATION_PREFERENCES } from "./query";

export const Account = ({
  customerInfoLoading,
  autopayPaperlessLoading,
  getAutopayPaperlessDetails,
  ordersLoading,
  billingLoading,
  handleCancelAutopay,
  cancelAutopayLoading,
  dialogData,
  setTurnOff,
  turnOff,
  showAutoPayError,
  setShowAutoPayError,
  plansServicesError,
  plansServicesLoading,
  plansServicesInfo,
  getPlansServices,
  billingError,
}: any) => {
  const customerDetails = useSelector((state: any) => state.customerInfoDetails);
  const { paymentWarning } = useSelector((state: any) => state?.userInfo);
  const { showAutopay, paymentsMigrationToNDS, NDSInprogressAccount } = useSelector(
    (state: any) => state.customerPlanDetails
  );
  const { autoPayData } = useSelector((state: any) => state?.autopayPaperlessDetails);
  const { activeAccount } = useSelector((state: any) => state?.customerInfoDetails);
  const {
    returnRequired: equipmentReturnRequired,
    apiError: equipmentReturnApiError,
    returnDate: equipmentReturnDate,
    status,
  } = useSelector((state: any) => state?.equipmentReturnDetails);
  const envFlags = useSelector((state: any) => state?.userInfo?.envFlags);
  const ENABLE_RETURN_EQUIPMENT = getFlags("enable_return_equipment", envFlags);
  const INACTIVE_ACCOUNTS = getFlags("inactive_accounts", envFlags);
  const hideInactiveFeature = INACTIVE_ACCOUNTS ? activeAccount !== false : true;
  const SHOW_CUSTOMER_SERVICE = getFlags("customer_subscribed_services", envFlags);
  const SHOW_DISABLE_AUTOPAY = getFlags("show_disableAutoPay_banner", envFlags);
  const { scrollPosition } = useSelector((state: any) => state.pageInfo);
  const sideCoreBannerMessages = useSelector(
    (state: any) => state?.sideCoreContent?.bannerMessages
  );
  const ACCOUNT_ACTIVATION_BANNER_MESSAGE = getBannerMessageByKey(
    "account-activation",
    sideCoreBannerMessages
  );
  const [
    getBillingPreferences,
    {
      error: billingPreferencesError,
      loading: billingPreferencesLoading,
      data: billingPreferencesInfo,
    },
  ] = useLazyQuery(GET_BILLING_PREFERENCES, {
    variables: {
      accountNumber: customerDetails?.accountNumber,
    },
  });

  const [
    getNotificationPreference,
    {
      error: notificationPreferenceError,
      loading: notificationPreferenceLoading,
      data: notificationPreference,
    },
  ] = useLazyQuery(GET_NOTIFICATION_PREFERENCES, {
    variables: {
      accountNumber: customerDetails?.accountNumber,
      contactId: customerDetails?.contactId,
    },
    fetchPolicy: "network-only",
  });

  const [getCustomerServices, { data: customerServicesData, error: customerServicesError }] =
    useLazyQuery(GET_CUSTOMER_SERVICES_API, {
      variables: {
        accountNumber: customerDetails?.accountNumber,
        isNds: customerDetails?.ndsObj?.isNDS,
      },
      fetchPolicy: "network-only",
    });

  const customerServices = customerServicesData?.customerSubscribedServices;

  const refreshPlansServices = () => {
    getPlansServices();
  };

  const refreshBillingPreferences = () => {
    getBillingPreferences({ fetchPolicy: "network-only" });
  };

  useEffect(() => {
    if (
      customerDetails?.accountNumber &&
      customerDetails?.ndsObj?.isNDS !== undefined &&
      SHOW_CUSTOMER_SERVICE
    ) {
      getCustomerServices();
    }
  }, [customerDetails?.accountNumber, customerDetails?.ndsObj?.isNDS]);

  const location = useLocation();
  const managePaymentMethodsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (location.state?.scrollToPaymentMethods) {
      setTimeout(() => {
        if (managePaymentMethodsRef.current) {
          const elementPosition = managePaymentMethodsRef.current.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - 150; // 150px offset for header

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [location.state?.scrollToPaymentMethods]);

  const billingAddress = billingPreferencesInfo?.billingPreferences?.billingAddrress;
  const planAndServicesInfo = plansServicesInfo?.plansAndServices;
  const notificationPref = notificationPreference?.notificationPreferences;
  const fallOut = plansServicesInfo?.plansAndServices[0]?.fallOut;
  const isEquipmentReturnPending = status?.toLowerCase() === "pending";
  const formattedEquipmentReturnDate = equipmentReturnDate
    ? moment(equipmentReturnDate).format("MMMM D, YYYY")
    : "";

  const equipmentReturnBadges = isEquipmentReturnPending ? (
    <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={{
          ml: 1,
          backgroundColor: "#FFF8E6",
          borderRadius: "16px",
          px: 1,
          py: 0.5,
        }}>
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: "#92400E",
            flexShrink: 0,
          }}
        />

        <Typography fontSize="11px" color="#92400E" fontWeight={600}>
          Action needed
        </Typography>
      </Stack>

      {formattedEquipmentReturnDate && (
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{
            backgroundColor: "#FFF8E6",
            borderRadius: "16px",
            px: 1,
            py: 0.5,
          }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: "#92400E",
              flexShrink: 0,
            }}
          />

          <Typography fontSize="11px" color="#92400E" fontWeight={600}>
            RETURN BY : {formattedEquipmentReturnDate}
          </Typography>
        </Stack>
      )}
    </Stack>
  ) : null;

  interface AccountComponentsProps {
    name?: string;
    component?: ReactJSXElement;
  }

  const accountComponents: AccountComponentsProps[] = [
    {
      name: ACCOUNT_SCREEN_COMPONENTS.KEY_ACCOUNT_INFO,
      component: <KeyAccountInfo customerInfoLoading={customerInfoLoading} />,
    },
    ...(equipmentReturnRequired && !equipmentReturnApiError && ENABLE_RETURN_EQUIPMENT
      ? [
          {
            name: ACCOUNT_SCREEN_COMPONENTS.YOUR_ZIPLY_DEVICES,
            component: <EquipmentReturnInfo />,
          },
        ]
      : []),
    {
      component: (
        <>
          {INACTIVE_ACCOUNTS &&
            SHOW_DISABLE_AUTOPAY &&
            activeAccount === false &&
            !customerInfoLoading &&
            autoPayData &&
            Object.keys(autoPayData)?.length > 0 && (
              <StyledCard>
                <Typography variant="h6" fontSize="1.3rem">
                  Disable your Autopay
                </Typography>
                <Typography
                  mb={1.5}
                  color={Theme.palette.customcolor.profileTypoGraphyColor}
                  fontSize={{ xs: "14px", sm: "16px" }}>
                  Autopay is currently active on your account. Since this account is inactive,
                  please turn off Autopay to avoid any issues.
                </Typography>
                <Box display="flex" justifyContent="center">
                  <Button
                    type="payment"
                    title="DISABLE AUTOPAY"
                    disabled={isAdmin}
                    onClick={handleCancelAutopay}
                  />
                </Box>
              </StyledCard>
            )}
        </>
      ),
    },
    {
      name: ACCOUNT_SCREEN_COMPONENTS.PROFILE,
      component: <AccountProfile />,
    },
    {
      name: ACCOUNT_SCREEN_COMPONENTS.BILLING_INFORMATION,
      component: (
        <AccountBillingPreference
          billingPreferencesLoading={billingPreferencesLoading}
          billingPreferencesError={billingPreferencesError}
          billingPreferencesInfo={billingPreferencesInfo}
          onRefresh={refreshBillingPreferences}
          getAutopayPaperlessDetails={getAutopayPaperlessDetails}
          billingAddress={billingAddress}
          managePaymentMethodsRef={managePaymentMethodsRef}
          shouldExpandPaymentMethods={location.state?.scrollToPaymentMethods}
        />
      ),
    },
    {
      component: (
        <>
          {hideInactiveFeature && (
            <>
              {autopayPaperlessLoading ? (
                <StyledCard sx={{ paddingY: 10 }}>
                  <Spinner loadingText={AUTOPAY_PAPERLESS_LOADING_MESSAGE} />
                </StyledCard>
              ) : (
                !(
                  (!paymentsMigrationToNDS && !customerDetails.ndsObj.isNDS) ||
                  (customerDetails.ndsObj.isNDS && NDSInprogressAccount)
                ) && (
                  <Grid mb={2} container justifyContent={"space-between"} gap={{ xs: 3, sm: 0 }}>
                    <Grid item xs={12} md={5.8}>
                      <AutoPay type={ACCOUNT} billingError={billingError} />
                    </Grid>
                    <Grid item xs={12} md={5.8}>
                      <PaperLessBilling billingError={billingError} />
                    </Grid>
                  </Grid>
                )
              )}
            </>
          )}
        </>
      ),
    },
    {
      name: hideInactiveFeature ? ACCOUNT_SCREEN_COMPONENTS.PLANS_SERVICES : "",
      component: (
        <>
          {hideInactiveFeature && (
            <AccountPlansServices
              autopayPaperlessLoading={autopayPaperlessLoading}
              plansServicesError={plansServicesError}
              plansServicesLoading={plansServicesLoading}
              onRefresh={refreshPlansServices}
              planAndServicesInfo={planAndServicesInfo}
              Internet={customerServices?.internet}
              Phone={customerServices?.phone}
              customerServicesError={customerServicesError}
              ordersLoading={ordersLoading}
              showCustomerSubscribedServices={SHOW_CUSTOMER_SERVICE}
              fallOut={fallOut}
            />
          )}
        </>
      ),
    },
    {
      name: ACCOUNT_SCREEN_COMPONENTS.ORDER_SERVICE_TICKET_HISTORY,
      component: <OrderAndServiceTicketHistory />,
    },

    {
      name: ACCOUNT_SCREEN_COMPONENTS.SETTINGS,
      component: (
        <Settings
          getNotificationPreference={getNotificationPreference}
          notificationPreferenceError={notificationPreferenceError}
          notificationPref={notificationPref}
          notificationPreferenceLoading={notificationPreferenceLoading}
        />
      ),
    },
  ];

  const blankAccountComponents: AccountComponentsProps[] = [
    {
      name: ACCOUNT_SCREEN_COMPONENTS.KEY_ACCOUNT_INFO,
      component: <KeyAccountInfo customerInfoLoading={customerInfoLoading} />,
    },
    {
      name: ACCOUNT_SCREEN_COMPONENTS.PROFILE,
      component: <AccountProfile />,
    },
    {
      name: ACCOUNT_SCREEN_COMPONENTS.SETTINGS,
      component: (
        <Settings
          getNotificationPreference={getNotificationPreference}
          notificationPreferenceError={notificationPreferenceError}
          notificationPref={notificationPref}
          notificationPreferenceLoading={notificationPreferenceLoading}
        />
      ),
    },
  ];

  useEffect(() => {
    if (customerDetails?.accountNumber?.trim()?.length > 0) {
      getBillingPreferences();
      getNotificationPreference();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerDetails?.accountNumber]);

  const autopayMessge = splitMessageBybraces(paymentWarning?.autopayMessage);
  const ShowAutopayContent = {
    showAutopay: true,
    title: autopayMessge[0],
    autopayDescription: autopayMessge[1],
    autoPaySubDiscription: autopayMessge[2],
  };

  return (
    <>
      <PageGrid container>
        <Grid item xs={12} lg={8.3}>
          {INACTIVE_ACCOUNTS &&
            activeAccount === false &&
            !customerInfoLoading &&
            !billingLoading &&
            customerDetails?.linkedAccountNumbers?.length > 0 && <InactivateAccountBanner />}

          {!showAutopay &&
            hideInactiveFeature &&
            !customerInfoLoading &&
            !billingLoading &&
            !billingError &&
            customerDetails?.accountNumber && (
              <HomepageNewBanners content={ACCOUNT_ACTIVATION_BANNER_MESSAGE?.content} />
            )}

          <PageNameTypography>Account</PageNameTypography>
          {customerDetails?.linkedAccountNumbers?.length > 0 ? (
            <>
              {accountComponents?.map(({ name, component }) => {
                return (
                  <Box key={name}>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" pb={1}>
                      <CardHeading variant="h6" fontWeight={600}>
                        {name}
                      </CardHeading>
                      {name === ACCOUNT_SCREEN_COMPONENTS.YOUR_ZIPLY_DEVICES &&
                        equipmentReturnBadges}
                    </Stack>
                    {component}
                  </Box>
                );
              })}
            </>
          ) : (
            <>
              {blankAccountComponents?.map(({ name, component }) => {
                return (
                  <Box key={name}>
                    <CardHeading variant="h6" fontWeight={600}>
                      {name}
                    </CardHeading>
                    {component}
                  </Box>
                );
              })}
            </>
          )}
          <ConfirmationModel
            dialogData={dialogData}
            openDialog={turnOff}
            setOpenDialog={setTurnOff}
            onClick={() => setTurnOff(false)}
          />
          <ErrorPopup openError={showAutoPayError} setOpenError={setShowAutoPayError} />
          {cancelAutopayLoading && <LoadingSpinner />}
        </Grid>
        <Grid item display={{ xs: "block", lg: "none" }} xs={12}>
          <NeedHelpCard />
        </Grid>
      </PageGrid>
      <StickyBox pt={1.5} display={{ xs: "none", lg: scrollPosition > 1200 ? "none" : "block" }}>
        <NeedHelpCard />
      </StickyBox>
    </>
  );
};

export const Container = styled(Box)(({ theme }) => ({
  padding: "1rem",
}));
